/**
 * Hook que carga los colores universales del calendario (definidos en
 * backend) y los aplica como CSS variables en `:root` para que el CSS
 * y los componentes inline puedan referenciarlos vía
 * `var(--cal-color-booking)`, etc.
 *
 * Por qué CSS vars y no Context/Theme:
 * - Permite que el CSS estático de `calendario.css` use los colores sin
 *   re-renderizar componentes.
 * - El primer paint usa los fallbacks hardcodeados (mismos que los defaults
 *   del backend), por lo que no hay parpadeo visible mientras carga el fetch.
 * - Cuando admin cambia un color, todos los usuarios que recarguen lo ven.
 *
 * Cache: respuesta por sesión. Se cachea en módulo (variable estática)
 * para que múltiples vistas que usen el hook no disparen N fetches.
 */

import { useEffect, useState } from "react";
import {
  configOptionApi,
  type CalendarColorsSettings,
} from "../../services/configOption.api";

// Defaults locales — DEBEN coincidir con CALENDAR_COLOR_DEFAULTS del backend
// (configOption.controller.js) y con los fallbacks de calendario.css. Si se
// cambia un valor aquí, cambiarlo en los tres lugares.
const FALLBACK_COLORS: CalendarColorsSettings = {
  booking: "#2EC5F5",
  cleaning: "#0EA5E9",
  blocked_admin: "#334155",
  blocked_client: "#94A3B8",
  maintenance_admin: "#6D28D9",
  maintenance_client: "#A78BFA",
};

const CSS_VAR_BY_KEY: Record<keyof CalendarColorsSettings, string> = {
  booking: "--cal-color-booking",
  cleaning: "--cal-color-cleaning",
  blocked_admin: "--cal-color-block-admin",
  blocked_client: "--cal-color-block-client",
  maintenance_admin: "--cal-color-maintenance-admin",
  maintenance_client: "--cal-color-maintenance-client",
};

let cached: CalendarColorsSettings | null = null;
let inflight: Promise<CalendarColorsSettings> | null = null;

const applyToRoot = (colors: CalendarColorsSettings) => {
  const root = document.documentElement;
  (Object.keys(colors) as Array<keyof CalendarColorsSettings>).forEach(
    (key) => {
      root.style.setProperty(CSS_VAR_BY_KEY[key], colors[key]);
    },
  );
};

const fetchColors = async (): Promise<CalendarColorsSettings> => {
  if (cached) return cached;
  if (inflight) return inflight;
  inflight = (async () => {
    const res = await configOptionApi.getCalendarColors();
    const colors = res?.data ?? FALLBACK_COLORS;
    cached = colors;
    applyToRoot(colors);
    inflight = null;
    return colors;
  })();
  return inflight;
};

/**
 * Limpia el cache local — útil después de que un admin actualiza los
 * colores en `/config` para que la próxima invocación del hook traiga el
 * valor fresco. Si el componente que actualiza ya está montado, también
 * conviene re-aplicar inmediatamente con `applyCalendarColorsImmediate`.
 */
export const invalidateCalendarColorsCache = () => {
  cached = null;
  inflight = null;
};

/**
 * Aplica un set de colores al `:root` sin pasar por la red. Se usa cuando
 * el admin guarda nuevos colores y queremos reflejar el cambio sin esperar
 * a que el cache se invalide y se vuelva a fetchear.
 */
export const applyCalendarColorsImmediate = (
  colors: CalendarColorsSettings,
) => {
  cached = colors;
  applyToRoot(colors);
};

export const useCalendarColors = (): CalendarColorsSettings => {
  const [colors, setColors] = useState<CalendarColorsSettings>(
    cached ?? FALLBACK_COLORS,
  );

  useEffect(() => {
    let alive = true;
    fetchColors()
      .then((c) => {
        if (alive) setColors(c);
      })
      .catch(() => {
        // Silencioso: si el fetch falla, los fallbacks ya están aplicados
        // como CSS vars (las defaults coinciden con los hardcoded del CSS).
      });
    return () => {
      alive = false;
    };
  }, []);

  return colors;
};
